import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

const RazorpayCheckout = () => {
  const { userId, planId } = useParams();
  const paymentButtonRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);

  // Function to load Razorpay script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  // Function to verify payment with the server
  const verifyPayment = async (paymentId) => {
    try {
      console.log("Verifying payment:", { paymentId, userId, planId });
      setLoading(true);

      // Make POST request to your server to verify the payment
      const response = await fetch('https://api.app.behooked.co/verify_payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          payment_id: paymentId,
          user_id: userId, plan_id: planId 
        }),
      });

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Payment verification response:", data);

      // Redirect based on the status
      if (data.status === "success") {
        console.log("Payment successful, redirecting to thank you page");
        window.location.replace('/thank-you');
      } else if (data.status === "warning") {
        console.log("Payment failed, redirecting to failed page");
        window.location.replace('/payment-failed');
      } else {
        // Handle other statuses if needed
        console.warn("Unknown payment status:", data.status);
        setError(`Payment verification returned unknown status: ${data.status}`);
      }
    } catch (err) {
      console.error('Error verifying payment:', err);
      setError(`Payment verification failed: ${err.message}`);
      window.location.replace('/payment-failed');
    }
  };

  // Function to initialize Razorpay
  const initializeRazorpay = (subscriptionId) => {
    if (!window.Razorpay) {
      setError('Razorpay SDK failed to load. Please check your internet connection.');
      return;
    }

    const options = {
      "key": "rzp_live_N2vMSKbw6ALOES",
      "subscription_id": subscriptionId,
      "name": "BeHooked",
      "description": "Subscription Payment",
      "image": "https://d1dgdtphnngtpp.cloudfront.net/website_static/favicon.PNG",
      "handler": function(response) {
        // Close the modal first - this is crucial
        // We'll manually hide the modal using DOM manipulation
        try {
          // Try to find and remove the Razorpay modal from the DOM
          const razorpayModal = document.querySelector('.razorpay-container');
          if (razorpayModal) {
            razorpayModal.style.display = 'none';
          }

          const razorpayBackdrop = document.querySelector('.razorpay-backdrop');
          if (razorpayBackdrop) {
            razorpayBackdrop.style.display = 'none';
          }

          console.log("Manually hidden Razorpay modal elements");
        } catch (err) {
          console.error("Error hiding Razorpay elements:", err);
        }

        // Handle successful payment
        console.log("Payment completed!", response);

        if (response.razorpay_payment_id) {
          // Verify the payment with our server
          verifyPayment(response.razorpay_payment_id);
        } else {
          console.error("No payment ID received from Razorpay");
          setError("Payment failed: No payment ID received");
          window.location.replace('/payment-failed');
        }
      },
      "modal": {
        "ondismiss": function() {
          console.log("Payment modal closed");
        },
        "escape": false, // Prevent closing by pressing ESC
        "animation": false // Disable animations for faster closing
      }
    };

    const rzp = new window.Razorpay(options);

    // Store in window for global access
    window.rzpInstance = rzp;

    rzp.on('payment.failed', function(response) {
      console.error("Payment failed", response.error);
      alert(`Payment failed: ${response.error.description}`);
      window.location.replace('/payment-failed');
    });

    // Store the Razorpay instance in the ref and attach click handler
    if (paymentButtonRef.current) {
      paymentButtonRef.current.onclick = function(e) {
        e.preventDefault();
        rzp.open();
      }
    }

    // Auto-open the payment modal
    setTimeout(() => {
      console.log("Auto-opening Razorpay payment modal");
      rzp.open();
    }, 1000);
  };

  // Function to fetch subscription data from server
  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);

      // Make POST request to your server with the user ID
      const response = await fetch('https://api.app.behooked.co/create_subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: userId, plan_id: planId }),
      });

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }
      const data = await response.json();
      console.log("Subscription data received:", data);

      setSubscriptionData(data);
      setLoading(false);

      // Once we have the subscription data, initialize Razorpay
      const success = await loadRazorpayScript();
      if (success && data.subscription_id) {
        console.log("Initializing Razorpay with subscription ID:", data.subscription_id);
        initializeRazorpay(data.subscription_id);
      } else {
        console.error("Failed to initialize Razorpay", { success, subscriptionId: data.subscription_id });
        if (!success) {
          setError("Failed to load Razorpay script");
        } else if (!data.subscription_id) {
          setError("No subscription ID received from server");
        }
      }
    } catch (err) {
      console.error('Error fetching subscription data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId && planId) {
      fetchSubscriptionData();
    } else {
      setError('Invalid user ID or plan ID');
    }

    // Cleanup function to close Razorpay modal if component unmounts
    return () => {
      if (window.rzpInstance) {
        window.rzpInstance.close();
      }
    };
  }, [userId, planId]);

  // Function to manually open the payment modal
  const openPaymentModal = () => {
    if (window.rzpInstance) {
      window.rzpInstance.open();
    }
  };

  if (loading) {
    return (
      <div className="razorpay-container">
        <h2>Loading...</h2>
        <p>Retrieving subscription information for user: {userId} and plan: {planId}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="razorpay-container">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => window.location.replace('/')} className="cancel-btn">
          Go back
        </button>
      </div>
    );
  }

  return (
    <div className="razorpay-container">
      <h2>Razorpay Checkout</h2>
      <p>User ID: {userId}</p>
      <p>Plan ID: {planId}</p>
      {subscriptionData && (
        <p>Subscription ID: {subscriptionData.subscription_id}</p>
      )}
      <p>Click the button below if the payment window doesn't open automatically.</p>
      <button ref={paymentButtonRef} onClick={openPaymentModal} id="rzp-button1" className="payment-btn">
        Pay Now
      </button>
      <button onClick={() => window.location.replace('/')} className="cancel-btn">
        Cancel and go back
      </button>
    </div>
  );
};

export default RazorpayCheckout;
